import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchRequestedCards, fetchFuelCards, checkCardStatus, issueCard, pingOnafriq, selectRequestedCards, selectFuelCards, selectCardsLoading, selectCardsError, selectServicePingStatus, toggleCardStatus, loadCardFunds, fetchCardTransactions, requestCard, createCardForClient, bulkImportCards, editCard, deleteCard } from '../../store/slices/cardsSlice';
import { fetchCompanies, selectCompanies } from '../../store/slices/companiesSlice';
import { CreditCard, AlertTriangle, Plus, MoreVertical, AlertCircle, Activity, CheckCircle2, XCircle, Building2, UserCircle, FileText, Mail, Phone, MapPin, Calendar, Fingerprint, User, Upload, CheckCircle, Trash2, ExternalLink } from 'lucide-react';
import { StatsCard } from '../ui';
import Button from '../ui/Button';
import { apiService, type AdminDashboardSummary } from '../../services/api';
import './CardManagementPage.css';

interface CardActionDropdownProps {
  cardId: string;
  cardReference?: string;
  status: string;
  onEdit?: (cardId: string) => void;
  onDelete?: (cardId: string) => void;
}

const CardActionDropdown: React.FC<CardActionDropdownProps> = ({ cardId, cardReference, status, onEdit, onDelete }) => {
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const handleAction = async (action: string) => {
    setIsOpen(false);

    if (action === 'edit-card') {
      onEdit?.(cardId);
      return;
    }

    if (action === 'delete-card') {
      onDelete?.(cardId);
      return;
    }

    if (action === 'check-status') {
      try {
        const result = await dispatch(checkCardStatus(cardId)).unwrap();
        alert(`Status check successful for card ${cardId}. Result: ${JSON.stringify(result)}`);
      } catch (err: any) {
        alert(err.message || 'Failed to check card status');
      }
      return;
    }

    if (action === 'top-up-balance') {
      if (!cardReference) return;
      const amountStr = window.prompt(`Enter amount to top up for card ${cardReference}:`);
      if (!amountStr) return;
      const amount = parseFloat(amountStr);
      if (isNaN(amount) || amount <= 0) {
        alert("Invalid amount.");
        return;
      }
      try {
        await dispatch(loadCardFunds({ cardReference, amount })).unwrap();
        alert(`Successfully topped up card ${cardReference} with ₦${amount}`);
        dispatch(fetchFuelCards());
      } catch (err: any) {
        alert(err || 'Failed to top up card');
      }
      return;
    }

    if (action === 'view-details') {
      if (!cardReference) return;
      try {
        const transactions = await dispatch(fetchCardTransactions(cardReference)).unwrap();
        alert(`Transactions for card ${cardReference}:\n${JSON.stringify(transactions, null, 2)}`);
      } catch (err: any) {
        alert(err || 'Failed to fetch card transactions');
      }
      return;
    }

    if (action === 'suspend-card' || action === 'block-card') {
      if (!cardReference) return;
      try {
        await dispatch(toggleCardStatus({ cardReference, action: 'LOCK' })).unwrap();
        alert(`Card ${cardReference} has been locked.`);
        dispatch(fetchFuelCards());
      } catch (err: any) {
        alert(err || 'Failed to lock card');
      }
      return;
    }

    if (action === 'activate-card') {
      if (!cardReference) return;
      try {
        await dispatch(toggleCardStatus({ cardReference, action: 'UNLOCK' })).unwrap();
        alert(`Card ${cardReference} has been unlocked.`);
        dispatch(fetchFuelCards());
      } catch (err: any) {
        alert(err || 'Failed to unlock card');
      }
      return;
    }

    console.log(`${action} for card ${cardId}`);
  };

  return (
    <div className="card-action-dropdown">
      <button
        className="action-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="More actions"
      >
        <MoreVertical size={16} />
      </button>

      {isOpen && (
        <>
          <div className="dropdown-overlay" onClick={() => setIsOpen(false)} />
          <div className="dropdown-menu">
            <button
              className="dropdown-item"
              onClick={() => handleAction('check-status')}
            >
              Check Status
            </button>
            <button
              className="dropdown-item"
              onClick={() => handleAction('view-details')}
            >
              View Details
            </button>
            <button
              className="dropdown-item"
              onClick={() => handleAction('top-up-balance')}
            >
              Top Up Balance
            </button>
            <button
              className="dropdown-item"
              onClick={() => handleAction('adjust-limits')}
            >
              Adjust Limits
            </button>
            {status === 'Active' && (
              <button
                className="dropdown-item"
                onClick={() => handleAction('suspend-card')}
              >
                Suspend Card
              </button>
            )}
            {status === 'Suspended' && (
              <button
                className="dropdown-item"
                onClick={() => handleAction('activate-card')}
              >
                Activate Card
              </button>
            )}
            <button
              className="dropdown-item"
              onClick={() => handleAction('edit-card')}
            >
              ✏️ Edit Card
            </button>
            <button
              className="dropdown-item danger"
              onClick={() => handleAction('delete-card')}
            >
              🗑️ Delete Card
            </button>
            <button
              className="dropdown-item danger"
              onClick={() => handleAction('block-card')}
            >
              Block Card
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const CardManagementPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const requestedCards = useAppSelector(selectRequestedCards);
  const fuelCards = useAppSelector(selectFuelCards);
  const isLoading = useAppSelector(selectCardsLoading);
  const error = useAppSelector(selectCardsError);
  const pingStatus = useAppSelector(selectServicePingStatus);
  const companies = useAppSelector(selectCompanies);

  const getCompanyName = (companyId: string) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.legalName : companyId || 'N/A';
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCardForEdit) return;
    
    try {
      const payload: any = {};
      if (editForm.companyId) payload.companyId = editForm.companyId;
      if (editForm.status) payload.status = editForm.status;
      if (editForm.vehicleId !== (selectedCardForEdit.vehicleId || '')) {
        payload.vehicleId = editForm.vehicleId.trim() || null;
      }
      
      await dispatch(editCard({ cardId: selectedCardForEdit.id, updates: payload })).unwrap();
      alert('Card updated successfully!');
      setIsEditModalOpen(false);
      dispatch(fetchFuelCards());
    } catch (err: any) {
      alert(err || 'Failed to update card');
    }
  };

  const handleDeleteCardClick = (card: any) => {
    setSelectedCardForDelete(card);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCardForDelete) return;
    try {
      setIsDeleting(true);
      await dispatch(deleteCard(selectedCardForDelete.id)).unwrap();
      setIsDeleteModalOpen(false);
      setSelectedCardForDelete(null);
      if (viewMode === 'requested') {
        dispatch(fetchRequestedCards());
      } else {
        dispatch(fetchFuelCards());
      }
      try {
        const data = await apiService.getDashboardSummary();
        setSummary(data);
      } catch {
        // ignore summary refresh error
      }
    } catch (err: any) {
      alert(err || 'Failed to delete card');
    } finally {
      setIsDeleting(false);
    }
  };


  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [viewMode, setViewMode] = useState<'all' | 'requested'>('all');
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [requestCompanyId, setRequestCompanyId] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCardForEdit, setSelectedCardForEdit] = useState<any>(null);
  const [editForm, setEditForm] = useState({ companyId: '', status: '', vehicleId: '' });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCardForDelete, setSelectedCardForDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedIssueCard, setSelectedIssueCard] = useState<any | null>(null);
  const [accountId, setCustomerId] = useState('');
  const [contractId, setContractId] = useState('');

  const [createCompanyId, setCreateCompanyId] = useState('');
  const [createCid, setCreateCid] = useState('');
  const [createContractId, setCreateContractId] = useState('');

  // Bulk import state
  const [createMode, setCreateMode] = useState<'single' | 'bulk'>('single');
  const [bulkCompanyId, setBulkCompanyId] = useState('');
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [bulkResult, setBulkResult] = useState<{ message: string; rowsDetected: number } | null>(null);

  const [summary, setSummary] = useState<AdminDashboardSummary | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setIsLoadingSummary(true);
        const data = await apiService.getDashboardSummary();
        setSummary(data);
      } catch (err) {
        console.error('Failed to load card dashboard summary:', err);
      } finally {
        setIsLoadingSummary(false);
      }
    };
    fetchSummary();
  }, []);

  useEffect(() => {
    if (viewMode === 'requested') {
      dispatch(fetchRequestedCards());
    } else {
      dispatch(fetchFuelCards());
    }
    if (companies.length === 0) {
      dispatch(fetchCompanies());
    }
  }, [dispatch, viewMode]);

  const filteredCards = (fuelCards.length > 0 ? fuelCards : []).filter((card: any) => {
    const matchesSearch =
      (card.maskedCardNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (card.cardReference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (card.companyId || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'All Status' || card.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const filteredRequestedCards = requestedCards.filter(card => {
    const matchesSearch =
      (card.maskedCardNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (card.cardReference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (card.companyId || '').toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const handleIssueCardClick = (card: any) => {
    setSelectedIssueCard(card);
    setCustomerId('');
    setContractId('');
    setIsIssueModalOpen(true);
    dispatch(pingOnafriq());
  };

  const handleIssueCardSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedIssueCard) return;

    if (window.confirm(`Are you sure you want to issue card ${selectedIssueCard.cardReference}? This will trigger the issuance process with the provider.`)) {
      try {
        await dispatch(issueCard({ id: selectedIssueCard.id, accountId, contractId })).unwrap();
        alert(`Card ${selectedIssueCard.cardReference} issued successfully!`);
        setIsIssueModalOpen(false);
        setSelectedIssueCard(null);
      } catch (err: any) {
        alert(err || 'Failed to issue card');
      }
    }
  };

  const handlePingService = async () => {
    try {
      await dispatch(pingOnafriq()).unwrap();
      alert('Onafriq service connectivity test successful!');
    } catch (err: any) {
      alert(err || 'Payment provider connectivity test failed');
    }
  };

  const handleRequestCardSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!requestCompanyId) {
      alert("Please select a company to request a card for.");
      return;
    }

    try {
      await dispatch(requestCard({ companyId: requestCompanyId })).unwrap();
      alert("Card requested successfully!");
      setIsRequestModalOpen(false);
      setRequestCompanyId('');
      if (viewMode === 'all') {
        setViewMode('requested');
      } else {
        dispatch(fetchRequestedCards());
      }
    } catch (err: any) {
      alert(err || 'Failed to request card');
    }
  };

  const handleCreateCardSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createCompanyId || !createCid) {
      alert("Please provide both a Company and an Account ID (CID).");
      return;
    }

    try {
      await dispatch(createCardForClient({
        companyId: createCompanyId,
        cid: createCid,
        contractId: createContractId || undefined
      })).unwrap();
      alert("Card created successfully!");
      setIsCreateModalOpen(false);
      setCreateCompanyId('');
      setCreateCid('');
      setCreateContractId('');
      setBulkResult(null);
      setBulkFile(null);
      setCreateMode('single');

      // Switch to "All Cards" view and refresh — the imported card is ACTIVE, not REQUESTED
      setViewMode('all');
      dispatch(fetchFuelCards());
    } catch (err: any) {
      alert(err || 'Failed to create card');
    }
  };

  if (isLoading) {
    return (
      <div className="card-management-page loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <div className="spinner" />
        <p>Loading cards...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-management-page error" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px' }}>
        <AlertCircle size={48} className="error-icon" style={{ color: '#ef4444' }} />
        <h2>Failed to load cards</h2>
        <p>{error}</p>
        <button onClick={() => dispatch(fetchRequestedCards())} className="retry-btn" style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="card-management-page">
      <div className="card-header">
        <div className="card-title">
          <CreditCard size={24} />
          <h1>Card Management</h1>
          <div className="view-toggle" style={{ display: 'flex', gap: '8px', marginLeft: '16px', background: '#f1f5f9', padding: '4px', borderRadius: '8px' }}>
            <button
              style={{ padding: '6px 16px', borderRadius: '6px', border: 'none', background: viewMode === 'all' ? 'white' : 'transparent', color: viewMode === 'all' ? '#0f172a' : '#64748b', fontWeight: viewMode === 'all' ? 500 : 400, cursor: 'pointer', boxShadow: viewMode === 'all' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
              onClick={() => setViewMode('all')}
            >
              All Cards
            </button>
            <button
              style={{ padding: '6px 16px', borderRadius: '6px', border: 'none', background: viewMode === 'requested' ? 'white' : 'transparent', color: viewMode === 'requested' ? '#0f172a' : '#64748b', fontWeight: viewMode === 'requested' ? 500 : 400, cursor: 'pointer', boxShadow: viewMode === 'requested' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
              onClick={() => setViewMode('requested')}
            >
              Requested Cards
            </button>
          </div>
        </div>
        <div className="card-header-actions" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div className="ping-status-indicator" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#64748b' }}>
            {pingStatus === 'loading' && <Activity size={16} className="animate-pulse" />}
            {pingStatus === 'success' && <CheckCircle2 size={16} style={{ color: '#22c55e' }} />}
            {pingStatus === 'failed' && <XCircle size={16} style={{ color: '#ef4444' }} />}
            <span style={{
              color: pingStatus === 'success' ? '#22c55e' : pingStatus === 'failed' ? '#ef4444' : '#64748b'
            }}>
              {pingStatus === 'idle' && 'Service Status: Unknown'}
              {pingStatus === 'loading' && 'Checking Connectivity...'}
              {pingStatus === 'success' && 'Onafriq: Connected'}
              {pingStatus === 'failed' && 'Onafriq: Offline'}
            </span>
          </div>
          <Button variant="outline" onClick={handlePingService} disabled={pingStatus === 'loading'}>
            <Activity size={16} />
            Ping Service
          </Button>
          <a
            href="https://cards.onafriqpartners.com/Visa/MerchantLite/LoadAuthApprove.aspx"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none' }}
          >
            <Button variant="outline">
              <ExternalLink size={16} />
              Load Funds
            </Button>
          </a>
          <Button variant="primary" onClick={() => setIsCreateModalOpen(true)}>
            <Plus size={16} />
            Create Card
          </Button>
        </div>
      </div>

      <div className="card-stats">
        <StatsCard
          title="Total Issued Cards"
          value={isLoadingSummary || !summary ? '...' : summary.totalCardsIssued.toLocaleString()}
          icon={<CreditCard size={24} />}
          color="blue"
          trend={{ label: 'Overall', type: 'neutral' }}
          footerMetrics={[]}
        />
        <StatsCard
          title="Active Cards"
          value={isLoadingSummary || !summary ? '...' : summary.activeCards.toLocaleString()}
          icon={<CheckCircle2 size={24} />}
          color="green"
          trend={{ label: 'Currently in use', type: 'neutral' }}
          footerMetrics={[]}
        />
        <StatsCard
          title="Suspended Cards"
          value={isLoadingSummary || !summary ? '...' : summary.suspendedCards.toLocaleString()}
          icon={<AlertTriangle size={24} />}
          color="orange"
          trend={{ label: 'Requires Attention', type: 'decrease' }}
          footerMetrics={[]}
        />
        <StatsCard
          title="Active Companies"
          value={isLoadingSummary || !summary ? '...' : summary.activeCompanies.toLocaleString()}
          icon={<Building2 size={24} />}
          color="purple"
          trend={{ label: 'Client Base', type: 'neutral' }}
          footerMetrics={[]}
        />
      </div>

      <div className="cards-section">
        <div className="section-header">
          <h2>{viewMode === 'all' ? 'All Cards' : 'Requested Cards'}</h2>
          <div className="filters-container">
            <div className="search-container">
              <input
                type="text"
                placeholder={viewMode === 'all' ? "Search cards, holders, companies..." : "Search references, companies..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            {viewMode === 'all' && (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="status-filter"
              >
                <option value="All Status">All Status</option>
                <option value="Active">Active</option>
                <option value="Suspended">Suspended</option>
                <option value="Blocked">Blocked</option>
              </select>
            )}
          </div>
        </div>

        <div className="cards-table">
          <table>
            <thead>
              <tr>
                {viewMode === 'all' ? (
                  <>
                    <th>Card Number</th>
                    <th>Holder</th>
                    <th>Company</th>
                    <th>Assigned Vehicle</th>
                    <th>Status</th>
                    <th>Balance</th>
                    <th>Last Used</th>
                    <th>Actions</th>
                  </>
                ) : (
                  <>
                    <th>Reference</th>
                    <th>Masked Card #</th>
                    <th>Company</th>
                    <th>Status</th>
                    <th>Requested At</th>
                    <th>Actions</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {viewMode === 'all' ? (
                filteredCards.length > 0 ? (
                  filteredCards.map((card: any) => (
                    <tr key={card.id}>
                      <td><span className="card-number">{card.maskedCardNumber || 'N/A'}</span></td>
                      <td>
                        <span className="holder-name">
                          {card.printDetails?.firstName 
                            ? `${card.printDetails.firstName} ${card.printDetails.lastName || ''}`.trim()
                            : (card.metadata?.holderProfile?.firstName 
                                ? `${card.metadata.holderProfile.firstName} ${card.metadata.holderProfile.lastName || ''}`.trim()
                                : 'N/A')}
                        </span>
                      </td>
                      <td><span className="company-name">{getCompanyName(card.companyId)}</span></td>
                      <td>
                        <span className="vehicle-name" style={{ fontSize: 13, color: card.vehicle ? '#111827' : '#9ca3af' }}>
                          {card.vehicle?.plateNumber ? (
                            <span>
                              <strong style={{ fontFamily: 'monospace' }}>{card.vehicle.plateNumber}</strong>
                              {card.vehicle.make && <span style={{ fontSize: 11, color: '#6b7280', display: 'block' }}>{card.vehicle.make} {card.vehicle.model || ''}</span>}
                            </span>
                          ) : card.vehicleId ? (
                            <span style={{ fontFamily: 'monospace' }} title={card.vehicleId}>{card.vehicleId.slice(0, 8)}…</span>
                          ) : (
                            'Unassigned'
                          )}
                        </span>
                      </td>
                      <td><span className={`status-badge ${card.status?.toLowerCase()}`}>{card.status}</span></td>
                      <td><span className="balance-value">{card.balance || '₦0.00'}</span></td>
                      <td><span className="date-value">{card.activatedAt ? new Date(card.activatedAt).toLocaleDateString() : 'Never'}</span></td>
                      <td><div className="card-actions"><CardActionDropdown 
                            cardId={card.id} 
                            cardReference={card.cardReference} 
                            status={card.status} 
                            onEdit={() => {
                              setSelectedCardForEdit(card);
                              setEditForm({ 
                                companyId: card.companyId || '', 
                                status: card.status?.toLowerCase() || 'active', 
                                vehicleId: card.vehicleId || '' 
                              });
                              setIsEditModalOpen(true);
                            }}
                            onDelete={() => handleDeleteCardClick(card)}
                          /></div></td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: '24px' }}>No cards found</td></tr>
                )
              ) : (
                filteredRequestedCards.length > 0 ? (
                  filteredRequestedCards.map((card) => (
                    <tr key={card.id}>
                      <td><span className="card-number">{card.cardReference}</span></td>
                      <td><span className="card-number">{card.maskedCardNumber || 'N/A'}</span></td>
                      <td><span className="company-name">{getCompanyName(card.companyId)}</span></td>
                      <td><span className="status-badge pending">{card.status}</span></td>
                      <td><span className="date-value">{new Date(card.createdAt).toLocaleDateString()}</span></td>
                      <td>
                        <div className="card-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleIssueCardClick(card)}
                          >
                            Issue Card
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            style={{ color: '#dc2626', borderColor: '#fca5a5' }}
                            onClick={() => handleDeleteCardClick(card)}
                            title="Delete card request"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '24px' }}>No requested cards found</td></tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isRequestModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content premium-modal" style={{ backgroundColor: '#ffffff' }}>
            <div className="modal-header">
              <h2>Request New Card</h2>
              <button
                className="close-button"
                onClick={() => setIsRequestModalOpen(false)}
                aria-label="Close modal"
              >
                <XCircle size={20} />
              </button>
            </div>

            <form onSubmit={handleRequestCardSubmit}>
              <div className="modal-body custom-scroll">
                <div className="form-group full-width">
                  <label>Assign to Company</label>
                  <div className="input-with-icon">
                    <Building2 size={18} className="input-icon" />
                    <select
                      value={requestCompanyId}
                      onChange={(e) => setRequestCompanyId(e.target.value)}
                      required
                    >
                      <option value="">Select a company</option>
                      {companies.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.legalName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsRequestModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isLoading || !requestCompanyId}
                >
                  {isLoading ? 'Requesting...' : 'Submit Request'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isIssueModalOpen && selectedIssueCard && (
        <div className="modal-overlay">
          <div className="modal-content premium-modal" style={{ maxWidth: '600px', backgroundColor: '#ffffff' }}>
            <div className="modal-header">
              <h2>Issue Card: {selectedIssueCard.cardReference}</h2>
              <button
                className="close-button"
                onClick={() => setIsIssueModalOpen(false)}
                aria-label="Close modal"
              >
                <XCircle size={20} />
              </button>
            </div>

            <form onSubmit={handleIssueCardSubmit}>
              <div className="modal-body custom-scroll">
                {/* Print Details Premium Section */}
                {selectedIssueCard.printDetails && Object.keys(selectedIssueCard.printDetails).length > 0 && (
                  <div className="holder-details-card">
                    <div className="details-card-header">
                      <UserCircle size={18} />
                      <h3>Card Holder Information</h3>
                    </div>
                    <div className="details-grid">
                      <div className="detail-item">
                        <div className="detail-icon"><User size={14} /></div>
                        <div className="detail-content">
                          <label>Full Name</label>
                          <span>{selectedIssueCard.printDetails.firstName} {selectedIssueCard.printDetails.lastName}</span>
                        </div>
                      </div>
                      <div className="detail-item">
                        <div className="detail-icon"><Calendar size={14} /></div>
                        <div className="detail-content">
                          <label>Birth Date</label>
                          <span>{selectedIssueCard.printDetails.birthDate || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="detail-item">
                        <div className="detail-icon"><Mail size={14} /></div>
                        <div className="detail-content">
                          <label>Email Address</label>
                          <span>{selectedIssueCard.printDetails.emailAddress || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="detail-item">
                        <div className="detail-icon"><Phone size={14} /></div>
                        <div className="detail-content">
                          <label>Phone Number</label>
                          <span>{selectedIssueCard.printDetails.mobilePhoneNumber || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="detail-item">
                        <div className="detail-icon"><Fingerprint size={14} /></div>
                        <div className="detail-content">
                          <label>Identity ({selectedIssueCard.printDetails.idType === '1' ? 'Passport' : 'ID'})</label>
                          <span>{selectedIssueCard.printDetails.idValue || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="detail-item">
                        <div className="detail-icon"><MapPin size={14} /></div>
                        <div className="detail-content">
                          <label>Location</label>
                          <span>{selectedIssueCard.printDetails.city || 'N/A'}, {selectedIssueCard.printDetails.country || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Additional Inputs */}
                <div className="modal-form-section">
                  <h4 className="section-subtitle">System Identifiers</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Account ID</label>
                      <div className="input-with-icon">
                        <Fingerprint size={18} className="input-icon" />
                        <input
                          type="text"
                          placeholder="Digits from back of card"
                          value={accountId}
                          onChange={(e) => setCustomerId(e.target.value)}
                          required
                        />
                      </div>
                      <span className="input-help">Integer ID found on physical card</span>
                    </div>
                    <div className="form-group">
                      <label>Contract ID</label>
                      <div className="input-with-icon">
                        <FileText size={18} className="input-icon" />
                        <input
                          type="text"
                          placeholder="e.g. CTR-892"
                          value={contractId}
                          onChange={(e) => setContractId(e.target.value)}
                          required
                        />
                      </div>
                      <span className="input-help">Linked service contract reference</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsIssueModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isLoading || !accountId || !contractId}
                >
                  {isLoading ? 'Issuing...' : 'Issue Card'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isCreateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content premium-modal" style={{ maxWidth: '600px', backgroundColor: '#ffffff' }}>
            <div className="modal-header">
              <h2>Card Setup</h2>
              <button
                className="close-button"
                onClick={() => { setIsCreateModalOpen(false); setBulkResult(null); setBulkFile(null); setCreateMode('single'); }}
                aria-label="Close modal"
              >
                <XCircle size={20} />
              </button>
            </div>

            {/* Mode Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '0 24px' }}>
              <button type="button"
                onClick={() => { setCreateMode('single'); setBulkResult(null); }}
                style={{ padding: '12px 20px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: createMode === 'single' ? 600 : 400, color: createMode === 'single' ? '#ff6b35' : '#6b7280', borderBottom: createMode === 'single' ? '2px solid #ff6b35' : '2px solid transparent', fontSize: '14px', transition: 'all 0.2s' }}
              >Single Card</button>
              <button type="button"
                onClick={() => { setCreateMode('bulk'); setBulkResult(null); }}
                style={{ padding: '12px 20px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: createMode === 'bulk' ? 600 : 400, color: createMode === 'bulk' ? '#ff6b35' : '#6b7280', borderBottom: createMode === 'bulk' ? '2px solid #ff6b35' : '2px solid transparent', fontSize: '14px', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px' }}
              ><Upload size={14} /> Bulk CSV Import</button>
            </div>

            {/* ── SINGLE CARD MODE ── */}
            {createMode === 'single' && (
              <form onSubmit={handleCreateCardSubmit}>
                <div className="modal-body custom-scroll">
                  <div className="form-group full-width" style={{ marginBottom: '24px' }}>
                    <label>Company Assignment</label>
                    <div className="input-with-icon">
                      <Building2 size={18} className="input-icon" />
                      <select value={createCompanyId} onChange={(e) => setCreateCompanyId(e.target.value)} required>
                        <option value="">Select a company</option>
                        {companies.map(c => <option key={c.id} value={c.id}>{c.legalName}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group full-width" style={{ marginBottom: '24px' }}>
                    <label>Account ID (CID)</label>
                    <div className="input-with-icon">
                      <Fingerprint size={18} className="input-icon" />
                      <input type="text" placeholder="e.g. 12345678" value={createCid} onChange={(e) => setCreateCid(e.target.value)} required />
                    </div>
                  </div>
                  <div className="form-group full-width" style={{ marginBottom: '24px' }}>
                    <label>Contract ID</label>
                    <div className="input-with-icon">
                      <FileText size={18} className="input-icon" />
                      <input type="text" placeholder="e.g. CTR-892" value={createContractId} onChange={(e) => setCreateContractId(e.target.value)} />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <Button type="button" variant="outline" onClick={() => setIsCreateModalOpen(false)}>Cancel</Button>
                  <Button type="submit" variant="primary" disabled={isLoading || !createCompanyId}>
                    {isLoading ? 'Creating...' : 'Create Card'}
                  </Button>
                </div>
              </form>
            )}

            {/* ── BULK CSV IMPORT MODE ── */}
            {createMode === 'bulk' && (
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!bulkFile || !bulkCompanyId) return;
                try {
                  const result = await dispatch(bulkImportCards({ file: bulkFile, companyId: bulkCompanyId })).unwrap();
                  setBulkResult(result);
                } catch (err: any) {
                  alert(err || 'Bulk import failed');
                }
              }}>
                <div className="modal-body custom-scroll">
                  {/* Company selector */}
                  <div className="form-group full-width" style={{ marginBottom: '20px' }}>
                    <label>Assign to Company</label>
                    <div className="input-with-icon">
                      <Building2 size={18} className="input-icon" />
                      <select value={bulkCompanyId} onChange={(e) => setBulkCompanyId(e.target.value)} required>
                        <option value="">Select a company</option>
                        {companies.map(c => <option key={c.id} value={c.id}>{c.legalName}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* CSV Format hint */}
                  <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#0369a1' }}>
                    <strong>Expected CSV format:</strong>
                    <pre style={{ margin: '8px 0 0', fontFamily: 'monospace', fontSize: '12px', color: '#075985' }}>{`accountId,contractId\n2147483647,CONTRACT-001\n2147483648,CONTRACT-002`}</pre>
                  </div>

                  {/* Drop zone / success panel */}
                  {!bulkResult ? (
                    <div
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={(e) => {
                        e.preventDefault(); setIsDragging(false);
                        const dropped = e.dataTransfer.files[0];
                        if (dropped?.name.endsWith('.csv')) setBulkFile(dropped);
                        else alert('Please drop a valid .csv file');
                      }}
                      onClick={() => document.getElementById('csv-file-input')?.click()}
                      style={{
                        border: `2px dashed ${isDragging ? '#ff6b35' : bulkFile ? '#10b981' : '#d1d5db'}`,
                        borderRadius: '10px', padding: '36px 24px', textAlign: 'center',
                        background: isDragging ? '#fff7f5' : bulkFile ? '#f0fdf4' : '#f9fafb',
                        transition: 'all 0.2s', cursor: 'pointer'
                      }}
                    >
                      <input id="csv-file-input" type="file" accept=".csv" style={{ display: 'none' }}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) setBulkFile(f); }} />
                      {bulkFile ? (
                        <>
                          <CheckCircle size={32} color="#10b981" style={{ marginBottom: '8px' }} />
                          <p style={{ margin: 0, fontWeight: 600, color: '#065f46' }}>{bulkFile.name}</p>
                          <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>{(bulkFile.size / 1024).toFixed(1)} KB — click to change</p>
                        </>
                      ) : (
                        <>
                          <Upload size={32} color="#9ca3af" style={{ marginBottom: '8px' }} />
                          <p style={{ margin: 0, fontWeight: 500, color: '#374151' }}>Drag & drop your CSV here</p>
                          <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9ca3af' }}>or click to browse — .csv files only</p>
                        </>
                      )}
                    </div>
                  ) : (
                    <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '10px', padding: '24px', textAlign: 'center' }}>
                      <CheckCircle size={40} color="#16a34a" style={{ marginBottom: '12px' }} />
                      <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: '16px', color: '#15803d' }}>Import Started!</p>
                      <p style={{ margin: 0, fontSize: '14px', color: '#166534' }}>{bulkResult.message}</p>
                      <div style={{ marginTop: '16px', background: '#dcfce7', borderRadius: '8px', padding: '12px', display: 'inline-block' }}>
                        <span style={{ fontSize: '28px', fontWeight: 700, color: '#15803d' }}>{bulkResult.rowsDetected}</span>
                        <span style={{ fontSize: '13px', color: '#166534', marginLeft: '6px' }}>rows detected</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  {bulkResult ? (
                    <Button type="button" variant="primary" onClick={() => { setIsCreateModalOpen(false); setBulkResult(null); setBulkFile(null); dispatch(fetchFuelCards()); }}>
                      Done
                    </Button>
                  ) : (
                    <>
                      <Button type="button" variant="outline" onClick={() => { setIsCreateModalOpen(false); setBulkFile(null); }}>Cancel</Button>
                      <Button type="submit" variant="primary" disabled={isLoading || !bulkFile || !bulkCompanyId}>
                        {isLoading ? 'Importing...' : 'Import Cards'}
                      </Button>
                    </>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      )}


      {/* Edit Card Modal */}
      {isEditModalOpen && selectedCardForEdit && (
        <div className="modal-overlay">
          <div className="modal-content premium-modal" style={{ maxWidth: '500px', backgroundColor: '#ffffff' }}>
            <div className="modal-header">
              <h2>Edit Card</h2>
              <button className="close-button" onClick={() => setIsEditModalOpen(false)}><XCircle size={20} /></button>
            </div>
            
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body custom-scroll">
              <div className="form-group">
                <label>Card Number</label>
                <input type="text" value={selectedCardForEdit.maskedCardNumber || selectedCardForEdit.cardReference || ''} disabled className="form-input bg-gray-100" />
              </div>
              
              <div className="form-group">
                <label>Assign to Company</label>
                <select 
                  className="form-input"
                  value={editForm.companyId}
                  onChange={(e) => setEditForm({...editForm, companyId: e.target.value})}
                  required
                >
                  <option value="">Select Company...</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.legalName}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select 
                  className="form-input"
                  value={editForm.status}
                  onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                >
                  <option value="active">Active</option>
                  <option value="locked">Locked</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="expired">Expired</option>
                </select>
              </div>

              <div className="form-group">
                <label>Vehicle ID (Optional)</label>
                <input 
                  type="text" 
                  className="form-input"
                  placeholder="Enter Vehicle UUID or leave empty to unassign"
                  value={editForm.vehicleId}
                  onChange={(e) => setEditForm({...editForm, vehicleId: e.target.value})}
                />
              </div>
              </div>
              
              <div className="modal-footer">
                <Button type="button" variant="outline" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Card Confirmation Modal */}
      {isDeleteModalOpen && selectedCardForDelete && (
        <div className="modal-overlay">
          <div className="modal-content premium-modal" style={{ maxWidth: '440px', backgroundColor: '#ffffff' }}>
            <div className="modal-header">
              <h2 style={{ color: '#b91c1c', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertTriangle size={20} color="#dc2626" /> Delete Card Record
              </h2>
              <button className="close-button" onClick={() => setIsDeleteModalOpen(false)} aria-label="Close modal">
                <XCircle size={20} />
              </button>
            </div>
            
            <div className="modal-body custom-scroll" style={{ padding: '20px' }}>
              <p style={{ marginBottom: '12px', fontSize: '14px', color: '#374151' }}>
                Are you sure you want to permanently delete this card record?
              </p>
              <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: '4px' }}>
                  {selectedCardForDelete.maskedCardNumber || selectedCardForDelete.cardReference || selectedCardForDelete.id}
                </div>
                {selectedCardForDelete.companyId && (
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    Company: {getCompanyName(selectedCardForDelete.companyId)}
                  </div>
                )}
                {selectedCardForDelete.status && (
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    Status: <span style={{ textTransform: 'capitalize' }}>{selectedCardForDelete.status}</span>
                  </div>
                )}
              </div>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                ⚠️ This action will unlink any associated transactions and permanently remove this card record. This cannot be undone.
              </p>
            </div>

            <div className="modal-footer" style={{ borderTop: '1px solid #f3f4f6', padding: '16px 20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button type="button" variant="outline" onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}>
                Cancel
              </Button>
              <Button 
                type="button" 
                variant="primary" 
                onClick={handleConfirmDelete} 
                disabled={isDeleting}
                style={{ backgroundColor: '#dc2626', borderColor: '#dc2626', color: '#ffffff' }}
              >
                {isDeleting ? 'Deleting...' : 'Delete Card'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardManagementPage;
